import { ReactNode } from "react"
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon
} from "@heroicons/react/24/outline"
import { classNames } from "utils/ClassNames"

export type AlertVariant = "error" | "success" | "warning" | "info"

export interface AlertProps {
  variant?: AlertVariant
  /** Optional bold heading shown above the body. */
  title?: ReactNode
  children?: ReactNode
  className?: string
}

const variantStyles: Record<
  AlertVariant,
  { container: string; icon: string; Icon: typeof XCircleIcon }
> = {
  error: {
    container: "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200",
    icon: "text-red-500 dark:text-red-400",
    Icon: XCircleIcon
  },
  success: {
    container: "bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-200",
    icon: "text-green-500 dark:text-green-400",
    Icon: CheckCircleIcon
  },
  warning: {
    container: "bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-200",
    icon: "text-yellow-500 dark:text-yellow-400",
    Icon: ExclamationTriangleIcon
  },
  info: {
    container: "bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200",
    icon: "text-blue-500 dark:text-blue-400",
    Icon: InformationCircleIcon
  }
}

/** A coloured message box with a leading icon, for form/page-level feedback. */
export const Alert = ({ variant = "info", title, children, className }: AlertProps) => {
  const { container, icon, Icon } = variantStyles[variant]

  return (
    <div className={classNames("rounded-md p-4", container, className)}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={classNames("h-5 w-5", icon)} aria-hidden="true" />
        </div>
        <div className="ml-3 text-sm">
          {title && <h3 className="font-medium">{title}</h3>}
          {children && <div className={classNames(!!title && "mt-1")}>{children}</div>}
        </div>
      </div>
    </div>
  )
}
